import "./HeaderProjectNameComponent.scss";
import { ImageConfig } from "@/constants";
import { useSelector } from "react-redux";
import { useCallback, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { Settings, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useDispatch } from "react-redux";
import { updateProjectDetails } from "@/store/actions/projectdata.action.ts";
import { useQueryClient } from "@tanstack/react-query";
import { projectsListKey } from "@/screens/app/projects-list/ProjectsComponent";
import { IRootReducerState } from "@/store/reducers";
import { CommonService } from "@/services";
import commonService from "@/services/common.service";

const HeaderProjectNameComponent = () => {
    const currentProjectDetails = useSelector((state: any) => state.projectData.currentProjectDetails);
    const user = useSelector((state: IRootReducerState) => state.auth.user);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [isRenamingProject, setIsRenamingProject] = useState(false);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    // Function to truncate project name after 15 characters
    const getTruncatedName = useCallback((name: string) => {
        if (name.length <= 15) return name;
        return name.substring(0, 15) + "...";
    }, []);

    // Handle rename project
    const handleRenameProject = useCallback(() => {
        commonService.handleMixpanelTrack("Project name changed", {
            project_id: currentProjectDetails?.id,
            new_project_name: newProjectName.trim(),
            timestamp: new Date().toISOString()
        });
        if (newProjectName.trim() && currentProjectDetails?.id) {
            setIsRenamingProject(true);
            CommonService._project_service.updateProject(currentProjectDetails.id, { name: newProjectName.trim() })
                .then(() => {
                    // Update Redux store
                    dispatch(updateProjectDetails({ name: newProjectName.trim() }));

                    // Update projects list cache
                    queryClient.setQueryData([projectsListKey], (oldData: any) => {
                        if (!oldData || !Array.isArray(oldData)) return oldData;

                        return oldData.map((project: any) =>
                            project.id === currentProjectDetails.id
                                ? { ...project, name: newProjectName.trim() }
                                : project
                        );
                    });
                })
                .catch(() => {
                    commonService._alert.showToast("Error renaming project", "error");
                })
                .finally(() => {
                    setIsRenamingProject(false);
                    setIsRenameDialogOpen(false);
                    setNewProjectName('');
                });
        }
    }, [newProjectName, currentProjectDetails?.id, dispatch, queryClient]);

    // Open rename dialog
    const handleOpenRenameDialog = useCallback(() => {
        setNewProjectName(currentProjectDetails?.name || '');
        setIsRenameDialogOpen(true);
    }, [currentProjectDetails?.name]);

    // Open settings dialog
    const handleOpenSettings = useCallback(() => {
        // Trigger settings opening via communication service
        CommonService._communications.openSettingsSubject.next({ category: undefined });
    }, []);

    return (
        <div className="header-project-details">
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                    <div className="project-name-container flex items-center gap-2 cursor-pointer">
                        {/* SuperEngineer Logo */}
                        <div className="superengineer-logo">
                            <ImageConfig.Favicon className="h-6 w-6" />
                        </div>

                        {/* Project Name */}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="project-name">
                                    {getTruncatedName(currentProjectDetails?.name || 'Untitled')}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{currentProjectDetails?.name || 'Untitled'}</p>
                            </TooltipContent>
                        </Tooltip>

                        {/* Dropdown Arrow */}
                        <ChevronDown size={14} className="text-muted-foreground" />
                    </div>
                </PopoverTrigger>

                <PopoverContent className="project-popover-container">
                    <div className="project-popover-content">


                        {/* User Info */}
                        <div className="user-section">
                            <div className="user-name">{user?.name || 'User'}</div>
                        </div>

                        {/* Credits Section */}
                        {/* <div className="credits-section">
                            <div className="credits-header">
                                <span className="credits-label">Credits</span>
                                <span className="credits-count">5 left</span>
                            </div>
                            <div className="credits-progress">
                                <div className="credits-progress-bar"></div>
                            </div>
                            <div className="credits-reset">
                                <div className="credits-reset-icon"></div>
                                <span>Daily credits reset at midnight UTC</span>
                            </div>
                        </div> */}

                        {/* Action Items */}
                        <div className="action-items">

                            <div className="dropdown-item active" onClick={handleOpenSettings}>
                                <Settings size={16} />
                                <span>Settings</span>
                            </div>

                            <div className="dropdown-item" onClick={handleOpenRenameDialog}>
                                <Pencil size={16} />
                                <span>Rename project</span>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            {/* Rename Project Dialog */}
            <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
                <DialogContent className="project-rename-dialog-content">
                    <DialogHeader>
                        <DialogTitle>Rename Project</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="project-name" className="text-sm font-medium mb-2 block">
                                Project Name
                            </label>
                            <Input
                                id="project-name"
                                value={newProjectName}
                                onChange={(value: string) => setNewProjectName(value)}
                                placeholder="Enter project name"
                                className="w-full"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleRenameProject();
                                    }
                                }}
                                
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsRenameDialogOpen(false);
                                    setNewProjectName('');
                                }}
                                disabled={isRenamingProject}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleRenameProject}
                                disabled={!newProjectName.trim() || isRenamingProject}
                                loading={isRenamingProject}
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default HeaderProjectNameComponent;
